/**
 * Autosave for the map estimator (Bug #388). The shapes being drawn are kept
 * in localStorage, keyed by the quote (or, for a quote not yet saved, the
 * client), so a hot reload, an accidental Cancel, a refresh or a crash never
 * loses a marked-up property. Cleared once the quote itself is saved.
 */
const PREFIX = 'gid.estimator.';

export function draftKey({ quoteId, customerId, propertyId } = {}) {
  if (propertyId) return `${PREFIX}p${propertyId}`; // a letterbox in a drop (backlog #691)
  if (quoteId) return `${PREFIX}q${quoteId}`;
  if (customerId) return `${PREFIX}c${customerId}`;
  return null;
}

export function readDraft(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && Array.isArray(d.shapes) ? d : null;
  } catch { return null; }
}

export function writeDraft(key, shapes, extra = {}) {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify({ savedAt: new Date().toISOString(), shapes, ...extra })); } catch { /* storage unavailable */ }
}

export function clearDraft(key) {
  if (!key) return;
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** Clear every draft that could belong to this quote / client. */
export function clearDraftsFor({ quoteId, customerId } = {}) {
  clearDraft(draftKey({ quoteId }));
  clearDraft(draftKey({ customerId }));
}

/**
 * ISO instant for a server timestamp. SQLite's CURRENT_TIMESTAMP comes back as
 * "YYYY-MM-DD HH:MM:SS" in UTC with no zone marker — Date would read it as
 * local time (10 h out in Brisbane) and a fresh draft could look older than
 * the quote's save.
 */
export function serverTimeIso(v) {
  if (!v) return null;
  const s = String(v);
  const d = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s) ? new Date(`${s.replace(' ', 'T')}Z`) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** What "the same shapes" means: geometry + what they are, ignoring view state. */
export function shapesSignature(shapes) {
  return JSON.stringify((shapes || []).map((s) => [s.kind, s.role || 'surface', s.geojson, String(s.surface_rate_id || ''), s.soiling || 'light', s.height_m ?? null, s.width2_m ?? null, s.label || '', s.coats ?? null]));
}
